// Garmin Connect (spec §24): link the account (OAuth 1.0a through the API), unlink it, and send a route to
// Garmin Connect as a course. Whether the user is linked, and whether they subscribe, comes from their profile
// (GET /persons): Garmin is for subscribers, as in the legacy web app.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { rememberReturnTo } from '@/auth/returnTo';

export const GARMIN_CALLBACK_PATH = '/garmin/callback';

export interface GarminStatus {
  connected: boolean;
  /** An active subscription (Garmin needs one). */
  subscriber: boolean;
}

/** A failed Garmin call; `code` is the API's message code (1050 subscription, 1051 not connected, …). */
export class GarminError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`Garmin: ${status}${code ? ` (${code})` : ''}`);
  }
}

interface ProfileJson {
  subscriptions?: unknown[];
  connectedAppAccounts?: { appName?: string; isConnected?: boolean }[];
}

const statusKey = ['garmin', 'status'];

export function useGarminStatus() {
  return useQuery<GarminStatus>({
    queryKey: statusKey,
    queryFn: async () => {
      const response = await apiFetch('/persons');
      if (!response.ok) throw new GarminError(response.status);
      const body = (await response.json()) as ProfileJson[] | ProfileJson;
      const person = Array.isArray(body) ? body[0] : body;
      const garmin = person?.connectedAppAccounts?.find((a) => /garmin/i.test(a.appName ?? ''));
      return {
        connected: garmin?.isConnected === true,
        subscriber: (person?.subscriptions?.length ?? 0) > 0,
      };
    },
  });
}

async function call(path: string, init: RequestInit): Promise<Response> {
  const response = await apiFetch(`/integrations/garmin${path}`, {
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string };
    throw new GarminError(response.status, body.message);
  }
  return response;
}

/** Starts linking: goes to Garmin to approve, which returns to /garmin/callback, then back to this page. */
export function useConnectGarmin() {
  return useMutation<void, GarminError>({
    mutationFn: async () => {
      const callbackUrl = `${window.location.origin}${GARMIN_CALLBACK_PATH}`;
      const response = await call('/connect', { method: 'POST', body: JSON.stringify({ callbackUrl }) });
      const { confirmUrl } = (await response.json()) as { confirmUrl: string };
      rememberReturnTo(`${window.location.pathname}${window.location.search}`);
      window.location.assign(confirmUrl);
    },
  });
}

/** Finishes linking with what Garmin sent to the callback page. */
export async function completeGarminLink(oauthToken: string, oauthVerifier: string) {
  await call('/complete', { method: 'POST', body: JSON.stringify({ oauthToken, oauthVerifier }) });
}

export function useDisconnectGarmin() {
  const queryClient = useQueryClient();
  return useMutation<void, GarminError>({
    mutationFn: async () => {
      await call('', { method: 'DELETE' });
    },
    onSuccess: () =>
      queryClient.setQueryData<GarminStatus>(statusKey, (s) => s && { ...s, connected: false }),
  });
}

export function useSendToGarmin(routeGuid: string) {
  const queryClient = useQueryClient();
  return useMutation<void, GarminError>({
    mutationKey: ['garmin', 'send', routeGuid],
    mutationFn: async () => {
      await call(`/routes/${routeGuid}`, { method: 'POST' });
    },
    onError: (error) => {
      // Garmin revoked the link: show "Connect" again.
      if (error.code === '1051') void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });
}

export const invalidateGarminStatus = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: statusKey });
